import { ChevronLeft, Calendar, Beaker, Calculator, TestTube, History, User, Bell, ExternalLink, CheckCircle2, Clock, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const SAMPLE_PROTOCOLS_URL = "https://drive.google.com/drive/folders/1pSJamLikTEEGbxStFgo2WVHPEVdsCda2";

export default function HelpGuidePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="help-guide-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-9 w-9 rounded-full"
            data-testid="help-back-btn"
          >
            <ChevronLeft size={20} />
          </Button>
          <h1 className="font-heading text-lg font-bold">Help & Guide</h1>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="p-4 pb-8 space-y-6 overflow-y-auto">
        {/* App Overview */}
        <section className="space-y-4">
          <h2 className="font-heading font-bold text-xl">App Overview</h2>
          
          <div className="space-y-3">
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Calendar size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Today</h3>
                  <p className="text-sm text-muted-foreground">View and log your daily doses. Track your progress and mark doses as complete.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Beaker size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Protocols</h3>
                  <p className="text-sm text-muted-foreground">Manage your injection schedules, create new protocols, and track cycle progress.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Calculator size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Calc</h3>
                  <p className="text-sm text-muted-foreground">Calculate reconstitution ratios and dose measurements for your compounds.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <TestTube size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Vials</h3>
                  <p className="text-sm text-muted-foreground">Track your reconstituted vials, monitor freshness, and estimate remaining doses.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <History size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">History</h3>
                  <p className="text-sm text-muted-foreground">View your past dose activity, filter by date or protocol, and export data.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Profile</h3>
                  <p className="text-sm text-muted-foreground">Manage your account settings, profile photo, security, and app preferences.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Core Features */}
        <section className="space-y-4">
          <h2 className="font-heading font-bold text-xl">Core Features</h2>
          
          <div className="space-y-4">
            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Beaker size={18} className="text-primary" />
                <h3 className="font-semibold">What is a Protocol?</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                A protocol is your injection schedule. It defines the compound, dose amount, frequency (daily, every other day, weekly, etc.), and duration of your cycle.
              </p>
            </div>

            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={18} className="text-primary" />
                <h3 className="font-semibold">How to Log a Dose</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Go to the <strong>Today</strong> tab → Find your scheduled dose → Tap the circle on the left to mark it as done. The dose will be logged with the current time.
              </p>
            </div>

            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={18} className="text-primary" />
                <h3 className="font-semibold">Next Dose Countdown</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                The dashboard shows your next upcoming injection with a countdown timer so you never miss a dose.
              </p>
            </div>

            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-primary" />
                <h3 className="font-semibold">Today's Progress</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Track how many doses you've completed today with a visual progress bar. Celebrate when you complete all your daily doses!
              </p>
            </div>

            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={18} className="text-primary" />
                <h3 className="font-semibold">Upcoming (48 Hours)</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                See your upcoming doses for the next 48 hours so you can plan ahead. This helps you prepare vials and schedule your time.
              </p>
            </div>

            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Bell size={18} className="text-primary" />
                <h3 className="font-semibold">Reminders</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Enable push notifications to receive a reminder 1 hour before each scheduled dose. Turn this on in Settings.
              </p>
            </div>

            <div className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Beaker size={18} className="text-primary" />
                <h3 className="font-semibold">Editing a Protocol</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Go to <strong>Protocols</strong> tab → Find the protocol you want to edit → Tap the <strong>Edit</strong> button → Make your changes → Save.
              </p>
            </div>
          </div>
        </section>

        {/* Vials vs Protocols Section */}
        <section className="space-y-4">
          <h2 className="font-heading font-bold text-xl">Vials vs Protocols</h2>
          
          <div className="bg-card rounded-xl p-4 border border-border space-y-4">
            <p className="text-sm text-muted-foreground">
              Vials and protocols are <strong>not directly linked</strong> in LabStack, and this is intentional.
            </p>
            
            <p className="text-sm text-muted-foreground">
              A <strong>protocol</strong> represents your dosing schedule, while the <strong>vial tracker</strong> helps you monitor your reconstituted compounds.
            </p>
            
            <p className="text-sm text-muted-foreground">
              You may use multiple vials within a single protocol, especially if a vial runs out mid-cycle. Because of this, vials are kept separate to give you flexibility.
            </p>
            
            <div className="bg-muted/50 rounded-lg p-3 mt-3">
              <p className="text-sm font-medium mb-2">Use the Vial Tracker as a simple way to:</p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Track when a vial was reconstituted</li>
                <li>Monitor freshness</li>
                <li>Keep notes like storage or batch details</li>
              </ul>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This design allows you to manage your dosing and inventory independently without restrictions.
            </p>
            
            <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
              <p className="text-sm">
                <strong>Note:</strong> You do not need to log a vial to create or use a protocol.
              </p>
            </div>
          </div>
        </section>

        {/* Sample Protocols Link */}
        <section className="pt-4 pb-8">
          <a 
            href={SAMPLE_PROTOCOLS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Card className="bg-primary/10 border-primary/30 hover:bg-primary/15 transition-colors cursor-pointer">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">View sample protocols by Leks LD</h3>
                  <p className="text-sm text-muted-foreground">Browse example protocols and guides</p>
                </div>
                <ExternalLink size={20} className="text-primary" />
              </CardContent>
            </Card>
          </a>
        </section>
      </main>
    </div>
  );
}
